// src/components/questionanswer/QnAElasticSearch.jsx
import React, { useEffect, useMemo, useState } from "react";
import questions from "./qnaJSONdata/QnAElasticSearch.json";
import QnASideBarRight from "./QnASideBarRight";
import ScrollButton from "../../ScrollButton";

/**
 * QnAElasticSearch — inline Q&A with LEFT TOC + left Sponsored ad + right sidebar + keyword filter
 * - LEFT: "On this page" + Sponsored ad (AdSense)
 * - MAIN: inline questions + filter; header has Download PDF + Print
 * - RIGHT: existing sidebar (already has its own Sponsored slot)
 * - Ads are limited to Sponsored cards only
 */

const ELASTIC_PDF = "/pdfs/elasticsearch-interview-questions.pdf"; // place file in /public/pdfs/
const normalize = (s = "") => s.toLowerCase();

// ===== AdSense helpers (shared pattern) =====
const ADSENSE_CLIENT = "ca-pub-9033879555371063"; // <-- set your publisher id

function useAdSense(clientId) {
  useEffect(() => {
    if (!clientId) return;
    const existing = document.querySelector("script[data-adsbygoogle]");
    if (!existing) {
      const s = document.createElement("script");
      s.setAttribute("data-adsbygoogle", "true");
      s.async = true;
      s.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${clientId}`;
      s.crossOrigin = "anonymous";
      document.head.appendChild(s);
    } else {
      try { (window.adsbygoogle = window.adsbygoogle || []).push({}); } catch {}
    }
  }, [clientId]);
}

function AdSlot({
  adSlot = "5201219465",
  format = "auto",
  fullWidthResponsive = "true",
  style = { display: "block", minHeight: 90 },
  className = "",
}) {
  useEffect(() => {
    try { (window.adsbygoogle = window.adsbygoogle || []).push({}); } catch {}
  }, []);
  return (
    <ins
      className={`adsbygoogle ${className}`}
      style={style}
      data-ad-client={ADSENSE_CLIENT}
      data-ad-slot={adSlot}
      data-ad-format={format}
      data-full-width-responsive={fullWidthResponsive}
    />
  );
}

// ===== UI bits =====
function FilterBar({ value, onChange, total, shown }) {
  return (
    <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
      <input
        className="form-control form-control-sm"
        style={{ maxWidth: 420 }}
        type="search"
        placeholder="Filter (e.g., index, shards/replicas, analyzers, Query DSL, aggregations)…"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label="Filter questions"
      />
      <span className="text-muted small">Showing {shown} of {total}</span>
    </div>
  );
}

function QAInlineList({ items }) {
  return (
    <ol className="ps-3">
      {items.map((it, idx) => (
        <li key={it.id ?? idx} className="mb-3">
          <h5 className="mb-2">{it.question}</h5>
          <div className="text-body" style={{ whiteSpace: "pre-wrap" }}>{it.answer}</div>
          {it.code ? (
            <pre className="mt-2 mb-0" style={{ overflow: "auto" }}>
              <code>{it.code}</code>
            </pre>
          ) : null}
        </li>
      ))}
    </ol>
  );
}

function OnThisPageLeft({ pdfHref }) {
  return (
    <div className="card shadow-sm border-0">
      <div className="card-body">
        <h2 className="h6 text-uppercase text-muted mb-2">On this page</h2>
        <nav className="nav flex-column small">
          <a className="nav-link px-0" href="#elasticsearch">Elasticsearch Q&amp;A</a>
          <a
            className="nav-link px-0 mt-2 text-decoration-underline"
            href={pdfHref}
            download="TechVenture-Elasticsearch-Interview-Questions.pdf"
            aria-label="Download Elasticsearch interview questions PDF"
          >
            ⬇︎ Download PDF
          </a>
        </nav>
      </div>
    </div>
  );
}

function LeftSponsoredCard() {
  return (
    <div className="card shadow-sm border-0 mt-3">
      <div className="card-body">
        <h2 className="h6 text-uppercase text-muted mb-2">Sponsored</h2>
        <AdSlot adSlot="5201219465" />
        <div className="small text-muted mt-2">Ads help keep content free.</div>
      </div>
    </div>
  );
}

function TopActions({ pdfHref }) {
  return (
    <div className="d-flex gap-2">
      <a
        href={pdfHref}
        download="TechVenture-Elasticsearch-Interview-Questions.pdf"
        className="btn btn-outline-danger btn-sm"
        aria-label="Download Elasticsearch interview questions as PDF"
      >
        Download PDF
      </a>
      <button
        type="button"
        className="btn btn-outline-secondary btn-sm"
        onClick={() => window.print()}
        aria-label="Print this page"
      >
        Print
      </button>
    </div>
  );
}

// ===== Main component =====
export default function QnAElasticSearch() {
  useAdSense(ADSENSE_CLIENT);

  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const qn = normalize(query);
    if (!qn) return questions;
    return questions.filter((x) =>
      normalize(`${x.question} ${x.answer} ${x.code ?? ""}`).includes(qn)
    );
  }, [query]);

  return (
    <div className="container-fluid">
      <div className="row g-4 flex-xl-nowrap">
        {/* LEFT: On This Page + Sponsored (sticky) */}
        <aside className="d-none d-xl-block col-xl-2 pt-3">
          <div className="position-sticky" style={{ top: "1rem" }}>
            <OnThisPageLeft pdfHref={ELASTIC_PDF} />
            <LeftSponsoredCard />
          </div>
        </aside>

        {/* MAIN */}
        <main className="col-12 col-lg-8 col-xl-7 pt-3" role="main">
          <header className="mb-2 d-flex justify-content-between align-items-start">
            <div>
              <h1 className="h2 mb-1">Elasticsearch Interview Questions</h1>
              <p className="text-muted mb-0">
                Use the filter to quickly find topics like indices/mappings, shards &amp; replicas,
                analyzers/tokenizers, Query DSL, aggregations, routing, and tuning.
              </p>
            </div>
            <TopActions pdfHref={ELASTIC_PDF} />
          </header>

          <FilterBar value={query} onChange={setQuery} total={questions.length} shown={filtered.length} />

          <section id="elasticsearch" className="pt-2">
            <QAInlineList items={filtered} />
          </section>

          {/* No in-article ads — keep them in Sponsored cards only */}
          <footer className="border-top mt-4 pt-3 small text-muted">
            Tip: Call out write/read paths, refresh/flush/merge cycles, mapping choices,
            and how shard count/routing impact performance and reindexing.
          </footer>

          <ScrollButton />
        </main>

        {/* RIGHT: existing sidebar */}
        <aside className="col-12 col-lg-4 col-xl-3 pt-3 border-start">
          <QnASideBarRight />
        </aside>
      </div>
    </div>
  );
}
